import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { uid } from '../../../../lib/uid';

/**
 * Test data for the voluntary work form's Autogenerate.
 *
 * **Nothing here is resolved against a catalog**, because the row has no foreign keys — the
 * same property `samplePreviousEmployment` has, and for the same reason: an organization somebody
 * volunteered at is not one this system holds a record of.
 *
 * The rule that does bite is the form's own: a start date in the future or an end date before
 * the start is refused server-side, so the dates are generated as a real span in the past.
 */
export interface SampleVoluntaryWork {
  periodFrom: string;
  periodTo: string;
  organizationName: string;
  organizationAddress: string;
  numberOfHours: string;
  positionOrNature: string;
}

export function sampleVoluntaryWork(): SampleVoluntaryWork {
  const tag = uid().slice(0, 6).toUpperCase();

  /*
    Calendar dates built from parts, never `toISOString` — that converts through UTC and can
    hand the field the day before the one intended.
  */
  const today = todayCalendarDate();
  const [year, month, day] = today.split('-').map(Number);
  const pad = (n: number) => String(n).padStart(2, '0');

  // A span that ended before today and began before it ended, which is every date rule this
  // form has.
  const startYear = year - 3 - Math.floor(Math.random() * 5);
  const endYear = startYear + 1;

  return {
    periodFrom: `${startYear}-${pad(month)}-${pad(day)}`,
    periodTo: `${endYear}-${pad(month)}-${pad(day)}`,

    // Obviously synthetic on sight. The identity-store rule does not reach this row — an
    // organization names nobody — but a generated record that reads as real is still a record
    // somebody has to work out the provenance of later.
    organizationName: `Test Volunteer Organization ${tag}`,
    organizationAddress: 'Test City',

    // A plausible total for a year of weekend volunteering, and never zero: zero is a
    // meaningful answer on this field, so generating it would put a real claim on test data.
    numberOfHours: String(40 + Math.floor(Math.random() * 200)),

    // A position rather than a nature of work — the form accepts either, and a post is the
    // answer a reader is more likely to be checking the column for.
    positionOrNature: 'Volunteer Coordinator',
  };
}
